<template>
  <div class="table-container">
    <div class="select-all-container" v-if="showSelectAll">
      <div class="select-all-checkbox">
        <input 
          type="checkbox"
          :checked="selectedLessons.size === filteredLessons.length"
          @change="$emit('toggle-select-all')"
          id="select-all"
          aria-label="Select all lessons"
        >
        <label for="select-all" class="select-all-label">Select All Lessons</label>
      </div>
      
      <div class="bulk-actions" v-if="selectedLessons.size > 0">
        <button @click="showBulkActionsMenu = !showBulkActionsMenu" class="bulk-actions-button">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bulk-icon">
            <circle cx="12" cy="12" r="1"/>
            <circle cx="12" cy="5" r="1"/>
            <circle cx="12" cy="19" r="1"/>
          </svg>
          Bulk Actions
        </button>
        
        <div class="bulk-actions-menu" v-if="showBulkActionsMenu">
          <div class="bulk-action-item" @click="$emit('bulk-move')">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bulk-action-icon">
              <path d="M9 18l6-6-6-6"/>
            </svg>
            Move to Course
          </div>
          <div class="bulk-action-item" @click="$emit('bulk-status')">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bulk-action-icon">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            Update Status
          </div>
          <div class="bulk-action-item" @click="$emit('bulk-export')">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bulk-action-icon">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export Selected
          </div>
          <div class="bulk-action-item delete" @click="$emit('bulk-delete')">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bulk-action-icon">
              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
            Delete Selected
          </div>
        </div>
      </div>
    </div>

    <div class="table-wrapper">
      <table class="lessons-table">
        <thead>
          <tr>
            <th class="checkbox-column" v-if="showSelectAll">
              <input 
                type="checkbox"
                :checked="selectedLessons.size === filteredLessons.length"
                @change="$emit('toggle-select-all')"
                aria-label="Select all lessons"
              >
            </th>
            <th class="thumbnail-column">Thumbnail</th>
            <th class="title-column">
              <div class="th-content">
                Title
                <button @click="sortBy('title')" class="sort-button">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="sort-icon" :class="{ active: sortField === 'title' }">
                    <path d="M7 10l5-5 5 5M7 14l5 5 5-5"/>
                  </svg>
                </button>
              </div>
            </th>
            <th>Description</th>
            <th>
              <div class="th-content">
                Duration
                <button @click="sortBy('duration')" class="sort-button">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="sort-icon" :class="{ active: sortField === 'duration' }">
                    <path d="M7 10l5-5 5 5M7 14l5 5 5-5"/>
                  </svg>
                </button>
              </div>
            </th>
            <th>
              <div class="th-content">
                Status
                <button @click="sortBy('lesson_status')" class="sort-button">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="sort-icon" :class="{ active: sortField === 'lesson_status' }">
                    <path d="M7 10l5-5 5 5M7 14l5 5 5-5"/>
                  </svg>
                </button>
              </div>
            </th>
            <th>
              <div class="th-content">
                Course
                <button @click="sortBy('course.title')" class="sort-button">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="sort-icon" :class="{ active: sortField === 'course.title' }">
                    <path d="M7 10l5-5 5 5M7 14l5 5 5-5"/>
                  </svg>
                </button>
              </div>
            </th>
            <th class="actions-column">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="lesson in sortedLessons" :key="lesson.id">
            <td class="checkbox-column" v-if="showSelectAll">
              <input 
                type="checkbox" 
                :checked="selectedLessons.has(lesson.id)"
                @change="$emit('toggle-selection', lesson.id)"
                :aria-label="'Select ' + lesson.title"
              >
            </td>
            <td class="thumbnail-column">
              <img 
                :src="lesson.lesson_temp_file || 'https://via.placeholder.com/300x200?text=No+Thumbnail'" 
                :alt="lesson.title"
                class="thumbnail"
              >
            </td>
            <td class="title-column">{{ lesson.title }}</td>
            <td class="description-column">
              <div class="description-text">{{ lesson.description }}</div>
            </td>
            <td>{{ formatDuration(lesson.duration) }}</td>
            <td>
              <span class="status-badge" :class="lesson.lesson_status">
                {{ lesson.lesson_status }}
              </span>
            </td>
            <td>{{ lesson.course?.title || 'Unassigned' }}</td>
            <td class="actions-column">
              <div class="action-buttons">
                <button @click="$emit('play-lesson', lesson)" class="action-button play" title="Play Lesson">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="action-icon">
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
                </button>
                <button @click="$emit('view-lesson', lesson)" class="action-button view" title="View Details">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="action-icon">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </button>
                <button @click="$emit('edit-lesson', lesson)" class="action-button edit" title="Edit Lesson">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="action-icon">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button @click="$emit('delete-lesson', lesson)" class="action-button delete" title="Delete Lesson">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="action-icon">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LessonsTable',
  props: {
    filteredLessons: {
      type: Array,
      required: true
    },
    selectedLessons: {
      type: Set,
      required: true
    },
    showSelectAll: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showBulkActionsMenu: false,
      sortField: 'title',
      sortDirection: 'asc'
    }
  },
  computed: {
    sortedLessons() {
      return [...this.filteredLessons].sort((a, b) => {
        let aValue = this.sortField.includes('.') 
          ? this.sortField.split('.').reduce((obj, key) => obj?.[key], a)
          : a[this.sortField];
        let bValue = this.sortField.includes('.')
          ? this.sortField.split('.').reduce((obj, key) => obj?.[key], b)
          : b[this.sortField];

        // Handle null/undefined values
        if (!aValue && aValue !== 0) aValue = '';
        if (!bValue && bValue !== 0) bValue = '';

        // Compare values
        if (this.sortDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }
  },
  methods: {
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
    },
    formatDuration(seconds) {
      if (!seconds && seconds !== 0) return '0 min';
      return `${Math.round(seconds / 60)} min`;
    }
  }
}
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
}

.lessons-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

th {
  background: #f8fafc;
  padding: 1rem;
  font-weight: 600;
  color: #1e293b;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.th-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sort-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #64748b;
}

.sort-icon {
  width: 16px;
  height: 16px;
  transition: all 0.2s ease;
}

.sort-icon.active {
  color: #3b82f6;
}

td {
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  color: #475569;
}

.checkbox-column {
  width: 40px;
  text-align: center;
}

.checkbox-column input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.thumbnail-column {
  width: 100px;
}

.thumbnail {
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
}

.title-column {
  min-width: 200px;
  font-weight: 500;
  color: #1e293b;
}

.description-column {
  min-width: 300px;
}

.description-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.status-badge.uploaded {
  background: #dcfce7;
  color: #166534;
}

.status-badge.pending {
  background: #fef3c7;
  color: #9a3412;
}

.status-badge.completed {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.in_progress {
  background: #dbeafe;
  color: #1e40af;
}

.status-badge.not_started {
  background: #f1f5f9;
  color: #475569;
}

.actions-column {
  width: 160px;
  text-align: right;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.action-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(100%);
  transition: transform 0.2s ease;
}

.action-button:hover::before {
  transform: translateY(0);
}

.action-button.play {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
}

.action-button.play:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3);
}

.action-button.view {
  background: #fef3c7;
  color: #9a3412;
}

.action-button.view:hover {
  background: #fef2e2;
  transform: translateY(-1px);
}

.action-button.edit {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  box-shadow: 0 2px 4px rgba(5, 150, 105, 0.2);
}

.action-button.edit:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(5, 150, 105, 0.3);
}

.action-button.delete {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  box-shadow: 0 2px 4px rgba(220, 38, 38, 0.2);
}

.action-button.delete:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(220, 38, 38, 0.3);
}

.action-icon {
  width: 18px;
  height: 18px;
  stroke-width: 2;
  transition: transform 0.2s ease;
}

.action-button:hover .action-icon {
  transform: scale(1.1);
}

.select-all-container {
  display: flex;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  justify-content: space-between;
}

.select-all-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.select-all-label {
  font-size: 0.875rem;
  color: #475569;
  font-weight: 500;
}

.bulk-actions {
  position: relative;
}

.bulk-actions-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f1f5f9;
  color: #475569;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.bulk-actions-button:hover {
  background: #e2e8f0;
}

.bulk-icon {
  width: 16px;
  height: 16px;
}

.bulk-actions-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  z-index: 10;
}

.bulk-action-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.bulk-action-item:hover {
  background: #f8fafc;
}

.bulk-action-item.delete {
  color: #dc2626;
}

.bulk-action-icon {
  width: 18px;
  height: 18px;
}

@media (max-width: 1024px) {
  .description-column {
    min-width: 200px;
  }
}
</style> 